/**
 * BudgetActualGrid — bảng INPUT/OUTPUT (Budget vs Actual) của sheet "Production
 * Analysis", áp full template list page (skill `/table-style`):
 *   - FilterBar global Search (quét mọi cột) + nút Columns (ẩn/hiện + reorder).
 *   - Per-column filter ▽ trong header (text/checkboxes/number tự detect).
 *   - Sort click header + pagination toolbar.
 *   - Freeze cột định danh **Code** (1 cột, §7.6/§7.7) — header inline style.
 *
 * Giữ nguyên cấu trúc report: group header (Budget/Actual/Value/Quality), dòng
 * TOTAL (bình quân gia quyền theo Mt), band ngăn cách row-group (BOM/Other Output —
 * chỉ hiện ở thứ tự gốc, ẩn khi đang sort). Style kế toán theo [[reportUi]].
 */
import { Fragment, useMemo, useState, type CSSProperties } from 'react'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'

import {
  ColumnFilterIcon,
  ColumnFilterPopover,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  type ColumnFilterAnchorPosition,
} from '../../../../components/dataList'
import { useLocalDatasheetColumns } from '../../../../utils/datasheetColumns/useLocalDatasheetColumns'
import { getTableSortSuffix, sortTableRows, toggleTableSort, type TableSortState } from '../../../../utils/tableSort'
import { FuturesColumnsMenu } from '../../../futures/FuturesColumnsMenu'
import type { BudgetActualRow } from '../productionReportMock'
import { BlockTitle, NumText, PctText } from './reportUi'
import { COLORS } from './styleTokens'

/* ---------- column model ------------------------------------------------ */

type BAGroup = 'identity' | 'budget' | 'actual' | 'value' | 'quality'
type BAKind = 'text' | 'pct' | 'var' | 'usd' | 'usdPerMt' | 'mt' | 'plain'

interface BAColumn {
  key: string
  label: string
  group: BAGroup
  width: number
  kind: BAKind
}

// Cột chất lượng K→AM (29 chỉ tiêu) bám đúng thứ tự & nhãn header sheet.
const QUALITY_COLUMNS: BAColumn[] = (
  [
    ['mc', 'MC'],
    ['blacks', 'Blacks'],
    ['brokens', 'Brokens'],
    ['blackBroken', 'Black & Broken'],
    ['fm', 'FM'],
    ['mouldy', 'Mouldy'],
    ['brown', 'Brown'],
    ['partlyBlack', 'Partly Black'],
    ['immature', 'Immature'],
    ['brokenHalf', 'Broken >½'],
    ['pulperNipped', 'Pulper-nipped'],
    ['blotchy', 'Blotchy'],
    ['waxy', 'Waxy'],
    ['insect', 'Insect'],
    ['triagesDefects', 'Triages-Defects'],
    ['fb', 'FB'],
    ['overdry', 'Overdry'],
    ['cherry', 'Cherry'],
    ['yellowbeans', 'Yellowbeans'],
    ['polishDegree', 'Polish degree'],
    ['s20', 'S20'],
    ['s19', 'S19'],
    ['s18', 'S18'],
    ['s16', 'S16'],
    ['s15', 'S15'],
    ['s14', 'S14'],
    ['s13', 'S13'],
    ['s12', 'S12'],
    ['ltS12', '<S12'],
  ] as [string, string][]
).map(([key, label]) => ({ key, label, group: 'quality' as const, width: 78, kind: 'plain' as const }))

const BA_COLUMNS: BAColumn[] = [
  { key: 'code', label: 'Code', group: 'identity', width: 120, kind: 'text' },
  { key: 'category', label: 'Category', group: 'identity', width: 120, kind: 'text' },
  { key: 'premium', label: 'Premium', group: 'identity', width: 90, kind: 'usd' },
  { key: 'bomPct', label: 'BOM %', group: 'budget', width: 80, kind: 'pct' },
  { key: 'actualMt', label: 'Actual Mt', group: 'actual', width: 100, kind: 'mt' },
  { key: 'actualPct', label: 'Actual %', group: 'actual', width: 80, kind: 'pct' },
  { key: 'varPct', label: 'Var %', group: 'actual', width: 90, kind: 'var' },
  { key: 'valuePerMt', label: 'Value / Mt', group: 'value', width: 110, kind: 'usdPerMt' },
  { key: 'totalValue', label: 'Total value', group: 'value', width: 130, kind: 'usd' },
  ...QUALITY_COLUMNS,
]

const FROZEN_KEY = 'code'

/** Render 1 ô số theo kind — kế toán (NumText/PctText), âm → đỏ. */
function renderCell(val: unknown, kind: BAKind) {
  if (kind === 'text') return val == null ? '' : String(val)
  if (val == null || val === '') return ''
  const n = Number(val)
  switch (kind) {
    case 'pct':
    case 'var':
      return <PctText frac={n} />
    case 'usd':
      return <NumText n={n} d={0} usd />
    case 'usdPerMt':
      return <NumText n={n} d={2} usd />
    case 'mt':
      return <NumText n={n} d={3} />
    case 'plain':
      return <NumText n={n} d={2} />
    default:
      return String(val)
  }
}

function groupLabel(group: BAGroup): string {
  switch (group) {
    case 'identity': return ''
    case 'budget': return 'Budget'
    case 'actual': return 'Actual'
    case 'value': return 'Value'
    case 'quality': return 'Quality'
  }
}

/** Filter kind cho từng cột: số → 'number'; text → mặc định auto (code/category checkboxes). */
function toFilterKind(kind: BAKind): 'text' | 'number' {
  return kind === 'text' ? 'text' : 'number'
}

function aggregateTotals(rows: BudgetActualRow[]): Partial<BudgetActualRow> {
  if (rows.length === 0) return {}
  const mtTotal = rows.reduce((s, r) => s + (r.actualMt || 0), 0)
  const bomTotal = rows.reduce((s, r) => s + (r.bomPct || 0), 0)
  const actualPctTotal = rows.reduce((s, r) => s + (r.actualPct || 0), 0)
  const varTotal = rows.reduce((s, r) => s + (r.varPct || 0), 0)
  const valueTotal = rows.reduce((s, r) => s + (r.totalValue || 0), 0)
  const wavg = (key: keyof BudgetActualRow) =>
    mtTotal > 0 ? rows.reduce((s, r) => s + Number(r[key] ?? 0) * (r.actualMt || 0), 0) / mtTotal : 0
  const totals: Partial<BudgetActualRow> = {
    code: 'TOTAL',
    category: '',
    bomPct: bomTotal,
    actualMt: mtTotal,
    actualPct: actualPctTotal,
    varPct: varTotal,
    totalValue: valueTotal,
    valuePerMt: mtTotal > 0 ? valueTotal / mtTotal : 0,
  }
  // Tổng chỉ tiêu chất lượng = bình quân gia quyền theo khối lượng (sheet dòng 26/46).
  for (const col of QUALITY_COLUMNS) {
    ;(totals[col.key as keyof BudgetActualRow] as number) = wavg(col.key as keyof BudgetActualRow)
  }
  return totals
}

/* ---------- component --------------------------------------------------- */

interface Props {
  title: string
  rows: BudgetActualRow[]
  /** Loại bảng → nhãn band "Input"/"Output". */
  side: 'input' | 'output'
  /** Mã ĐỊNH NGHĨA TRONG BOM của PO (từ Data layer). Dòng code ∉ tập này = Other. */
  bomCodes: readonly string[]
  /** Unique key persist column visibility (vd 'factory.production-analysis.output'). */
  storageKey: string
}

export default function BudgetActualGrid({ title, rows: baseRows, side, bomCodes, storageKey }: Props) {
  // BOM vs Other suy ra từ BOM Code của PO (không hardcode per-row): code ∈ bomCodes
  // → "BOM <side>", ngoài đó → "Other <side>".
  const bomCodeSet = useMemo(() => new Set(bomCodes), [bomCodes])
  const sideLabel = side === 'input' ? 'Input' : 'Output'
  const bandFor = (code: string) => `${bomCodeSet.has(code) ? 'BOM' : 'Other'} ${sideLabel}`
  // Column visibility + reorder (local hook → ship BE /user-preferences/column-visibility).
  const columnsCtrl = useLocalDatasheetColumns<BAColumn>({ allColumns: BA_COLUMNS, storageKey })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  // Global search
  const [searchTerm, setSearchTerm] = useState('')
  const searchPopover = useFilterPopover<'search'>()

  // Per-column filters
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{ key: string; position: ColumnFilterAnchorPosition } | null>(null)

  // Sort + pagination
  const [sortState, setSortState] = useState<TableSortState<string> | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length

  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (r) => rowMatchesSearch(r, searchTerm, BA_COLUMNS) && rowMatchesAllColumnFilters(r, columnFilters.filters),
      ),
    [baseRows, searchTerm, columnFilters.filters],
  )

  const sortColumns = useMemo(
    () => BA_COLUMNS.map((c) => ({ key: c.key, kind: (c.kind === 'text' ? 'text' : 'number') as 'text' | 'number' })),
    [],
  )
  const sortedRows = useMemo(() => {
    // Thứ tự gốc (chưa sort): PARTITION theo band — `BOM <side>` trước, `Other
    // <side>` sau (stable). Đảm bảo mỗi band xuất hiện đúng 1 lần & liền khối,
    // bất kể order từ BE có lệch bomCodes hay không. Sort → để sortTableRows.
    if (sortState === null) {
      const bom: BudgetActualRow[] = []
      const other: BudgetActualRow[] = []
      for (const r of filteredRows) {
        const code = String((r as unknown as Record<string, unknown>).code ?? '')
        ;(bomCodeSet.has(code) ? bom : other).push(r)
      }
      return [...bom, ...other]
    }
    return sortTableRows(filteredRows, sortState, sortColumns, (row, key) => (row as unknown as Record<string, unknown>)[key as string])
  }, [filteredRows, sortState, sortColumns, bomCodeSet])

  // Pagination chỉ hiện khi đáng (báo cáo Production Analysis ≤ ~15 dòng → ẩn,
  // toolbar chỉ còn "Total: N" cho gọn).
  const PAGINATE_THRESHOLD = 20
  const paginate = baseRows.length > PAGINATE_THRESHOLD
  const pagedRows = useMemo(
    () =>
      !paginate || rowsPerPage === -1 ? sortedRows : sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [paginate, sortedRows, page, rowsPerPage],
  )

  // TOTAL tính trên TOÀN BỘ filteredRows (không chỉ trang hiện tại) → vẫn nghĩa lý.
  const totals = useMemo(() => aggregateTotals(filteredRows), [filteredRows])

  // Cột MỞ ĐẦU mỗi group (theo visibleColumns) → kẻ vạch ngăn group thay cho 1
  // hàng header riêng. Bỏ qua cột đầu bảng (không cần vạch ở mép trái).
  const groupStartKeys = useMemo(() => {
    const set = new Set<string>()
    let prev: BAGroup | null = null
    visibleColumns.forEach((col, i) => {
      if (i > 0 && col.group !== prev) set.add(col.key)
      prev = col.group
    })
    return set
  }, [visibleColumns])

  const clearAll = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    searchPopover.close()
  }

  const toggleSort = (key: string) => {
    setSortState((s) => toggleTableSort(s, key))
    setPage(0)
  }

  // Popover meta
  const popoverColumn = useMemo(
    () => (colPopover ? BA_COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo(() => {
    if (!popoverColumn) return 'text' as const
    const inferred = inferColumnFilterKind({ key: popoverColumn.key, kind: toFilterKind(popoverColumn.kind) } as never, baseRows)
    return inferred === 'none' ? 'text' : inferred
  }, [popoverColumn, baseRows])
  const popoverOptions = useMemo(
    () => (popoverKind === 'checkboxes' && colPopover ? distinctColumnValues(baseRows, colPopover.key) : undefined),
    [popoverKind, colPopover, baseRows],
  )

  // Band row-group chỉ hiện ở thứ tự gốc (không sort) — sort làm xáo trộn source.
  const showBands = sortState === null
  // Vị trí code trong visibleColumns (sticky chỉ áp khi code đang hiện).
  const codeVisible = visibleColumns.some((c) => c.key === FROZEN_KEY)

  return (
    <Box>
      <BlockTitle>{title}</BlockTitle>

      <FilterBar hasActiveFilters={hasActiveFilters} onClearAll={clearAll} activeFilterCount={activeFilterCount}>
        <FilterButton label="Search" active={!!searchTerm.trim()} onClick={(e) => searchPopover.open('search', e)} />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => openColMenu(e.currentTarget)}
          sx={filterBarBtnSx}
        >
          Columns
        </Button>
      </FilterBar>

      <Box className="app-dataframe app-dataframe--compact">
        <Box className="app-dataframe-toolbar" sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Total: {filteredRows.length}</Typography>
          {paginate ? (
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
            />
          ) : null}
        </Box>

        {/* Scroll wrapper hệ GRAY (.app-dataframe) — KHÔNG dùng class legacy
            `app-table-wrap` (stripe teal/gold) vì cột freeze (.app-sticky-col gray)
            sẽ lệch format với cột non-freeze. Theo /table-style §7.6. */}
        <Box sx={{ overflow: 'auto', bgcolor: '#fff' }}>
          <Table
            size="small"
            stickyHeader
            sx={{
              tableLayout: 'fixed',
              borderCollapse: 'separate',
              borderSpacing: 0,
              '& .MuiTableCell-root': { pl: 1, pr: 1, fontSize: '0.7rem' },
            }}
          >
            <TableHead>
              {/* 1 header row cream-gold (skill signature). Nội dung flex: label
                  ellipsis + sort ▲▼ + filter ▽ → không tràn cột hẹp. Freeze Code
                  (inline style §7.7). Group (Budget/Actual/…) đánh dấu bằng vạch
                  trái đổi màu theo group thay vì 1 hàng header riêng. */}
              <TableRow>
                {visibleColumns.map((col) => {
                  const isFrozen = col.key === FROZEN_KEY && codeVisible
                  const numeric = col.kind !== 'text'
                  const headerStyle: CSSProperties = {
                    width: col.width,
                    minWidth: col.width,
                    maxWidth: col.width,
                  }
                  if (isFrozen) {
                    headerStyle.position = 'sticky'
                    headerStyle.left = 0
                    headerStyle.top = 0
                    headerStyle.zIndex = 7
                    headerStyle.backgroundColor = '#fff9f4'
                    headerStyle.backgroundImage = 'linear-gradient(180deg, #fff9f4 0%, #f4df9c 100%)'
                  }
                  return (
                    <TableCell
                      key={col.key}
                      align={numeric ? 'right' : 'left'}
                      className="app-table-header-cell"
                      onClick={() => toggleSort(col.key)}
                      style={headerStyle}
                      sx={{
                        cursor: 'pointer',
                        userSelect: 'none',
                        ...(groupStartKeys.has(col.key) && { borderLeft: `2px solid ${COLORS.borderStrong}` }),
                        ...(isFrozen ? {} : { position: 'relative' }),
                      }}
                      title={`${groupLabel(col.group) ? groupLabel(col.group) + ' · ' : ''}${col.label}`}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, justifyContent: numeric ? 'flex-end' : 'flex-start', minWidth: 0 }}>
                        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                          {col.label}
                          {getTableSortSuffix(sortState, col.key)}
                        </Box>
                        <ColumnFilterIcon
                          active={columnFilters.isActive(col.key)}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                      </Box>
                    </TableCell>
                  )
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.map((row, idx) => {
                const rowBand = bandFor(row.code)
                const prevBand = idx > 0 ? bandFor(pagedRows[idx - 1].code) : null
                const band = showBands && rowBand !== prevBand ? rowBand : null
                return (
                  <Fragment key={`${row.code}-${idx}`}>
                    {band ? (
                      <TableRow>
                        <TableCell
                          colSpan={visibleColumns.length}
                          sx={{
                            p: 0,
                            bgcolor: COLORS.bgHeader,
                            borderTop: `1px solid ${COLORS.border}`,
                            borderBottom: `1px solid ${COLORS.border}`,
                          }}
                        >
                          <Box
                            sx={{
                              position: 'sticky',
                              left: 0,
                              display: 'inline-block',
                              px: 1,
                              py: 0.4,
                              fontSize: '0.64rem',
                              fontWeight: 800,
                              textTransform: 'uppercase',
                              letterSpacing: 0.6,
                              color: COLORS.primary,
                            }}
                          >
                            {band}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : null}
                    <TableRow hover>
                      {visibleColumns.map((col) => {
                        const isFrozen = col.key === FROZEN_KEY && codeVisible
                        return (
                          <TableCell
                            key={col.key}
                            align={col.kind === 'text' ? 'left' : 'right'}
                            // Freeze qua class `.app-sticky-col` (CSS lo position/left/bg +
                            // match hover/selected hệ gray) thay vì inline bgcolor → khớp
                            // format cột non-freeze. /table-style §7.6.
                            className={isFrozen ? 'app-sticky-col' : undefined}
                            style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                            sx={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              fontWeight: col.key === FROZEN_KEY ? 600 : undefined,
                              ...(groupStartKeys.has(col.key) && { borderLeft: `1px solid ${COLORS.border}` }),
                            }}
                          >
                            {renderCell(row[col.key as keyof BudgetActualRow], col.kind)}
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  </Fragment>
                )
              })}
              {filteredRows.length > 0 ? (
                <TableRow>
                  {visibleColumns.map((col) => {
                    const isFrozen = col.key === FROZEN_KEY && codeVisible
                    return (
                      <TableCell
                        key={col.key}
                        align={col.kind === 'text' ? 'left' : 'right'}
                        style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                        sx={{
                          fontWeight: 800,
                          whiteSpace: 'nowrap',
                          bgcolor: COLORS.bgSubtle,
                          borderTop: `1px solid ${COLORS.borderStrong}`,
                          borderBottom: 'none',
                          position: 'sticky',
                          bottom: 0,
                          zIndex: 3,
                          ...(groupStartKeys.has(col.key) && { borderLeft: `1px solid ${COLORS.border}` }),
                          ...(isFrozen && {
                            left: 0,
                            zIndex: 4,
                            borderRight: `1px solid ${COLORS.border}`,
                          }),
                        }}
                      >
                        {col.kind === 'text'
                          ? col.key === FROZEN_KEY
                            ? 'TOTAL'
                            : ''
                          : renderCell(totals[col.key as keyof BudgetActualRow], col.kind)}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} align="center" sx={{ py: 3, color: COLORS.textMuted }}>
                    No rows match the current filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Box>

      {/* Global Search popover */}
      <FilterPopover open={searchPopover.state !== null} anchorEl={searchPopover.state?.anchor ?? null} onClose={() => searchPopover.close()}>
        {searchPopover.isOpen('search') && (
          <SearchFilterContent
            value={searchTerm}
            onChange={(v) => {
              setSearchTerm(v)
              setPage(0)
            }}
            placeholder="Code, category…"
            onClear={() => searchPopover.close()}
          />
        )}
      </FilterPopover>

      {/* Per-column filter popover (1 instance) */}
      <ColumnFilterPopover
        open={colPopover !== null}
        anchorPosition={colPopover?.position ?? null}
        onClose={() => setColPopover(null)}
        label={popoverColumn?.label ?? ''}
        kind={popoverKind}
        value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
        onChange={(v) => {
          if (!colPopover) return
          columnFilters.setFilter(colPopover.key, v)
          setPage(0)
        }}
        options={popoverOptions}
      />

      {/* Column visibility menu */}
      <FuturesColumnsMenu controller={columnsCtrl} />
    </Box>
  )
}
